<app-dialog [open]="open()" (closed)="close()">
  <div dialog-header>
    <h2 class="text-lg font-semibold text-primary">New ticket</h2>
    <p class="mt-1 text-sm text-secondary">
      Start a support conversation with someone. They will be notified and can reply in their thread.
    </p>
  </div>

  <div dialog-body class="flex flex-col gap-4 text-left">
    <div>
      <label for="outbound-user-search" class="block text-sm font-medium text-primary">
        Send to
      </label>

      @if (selectedUser(); as user) {
        <div
          class="mt-1 flex items-center gap-3 rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2">
          <div
            class="size-10 shrink-0 overflow-hidden rounded-full bg-gray-200 ring-1 ring-gray-200 dark:bg-zinc-700 dark:ring-zinc-600">
            @if (user.photoURL) {
              <img
                [src]="imageService.getImageSource(user.photoURL, 'gallery')"
                [alt]="'Profile picture for ' + user.fullName"
                class="size-full object-cover" />
            } @else {
              <div class="flex size-full items-center justify-center text-secondary" aria-hidden="true">
                <svg class="size-6" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z" />
                </svg>
              </div>
            }
          </div>
          <div class="min-w-0 flex-1">
            <p class="m-0 truncate text-sm font-medium text-primary">{{ user.fullName }}</p>
            <p class="m-0 truncate text-xs text-secondary">{{ user.email }}</p>
          </div>
          <span
            class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-zinc-700 dark:text-zinc-300">
            {{ formatUserType(user.userType) }}
          </span>
          <button type="button" class="btn-basic-sm btn-secondary" (click)="changeUser()">
            Change
          </button>
        </div>
      } @else {
        <div class="relative mt-1">
          <input
            id="outbound-user-search"
            type="search"
            role="combobox"
            autocomplete="off"
            [attr.aria-expanded]="showResults()"
            [attr.aria-controls]="showResults() ? 'outbound-user-results' : null"
            aria-autocomplete="list"
            [attr.aria-activedescendant]="activeOptionId()"
            [ngModel]="searchTerm"
            (ngModelChange)="onSearchTermChange($event)"
            (keydown)="onSearchKeydown($event)"
            placeholder="Search by name, email, handle, or user ID"
            class="w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary" />

          @if (searching()) {
            <p class="mt-2 text-xs text-secondary" aria-live="polite">Searching...</p>
          }

          @if (searchError()) {
            <p class="mt-2 text-xs text-red-600" role="alert">{{ searchError() }}</p>
          }

          @if (showResults()) {
            @if (results().length === 0 && !searching()) {
              <p class="mt-2 text-xs text-secondary" aria-live="polite">No people match that search.</p>
            } @else {
              <ul
                id="outbound-user-results"
                role="listbox"
                aria-label="Search results"
                class="mt-2 max-h-64 overflow-y-auto rounded-md border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
                @for (user of results(); track user.userId; let i = $index) {
                  <li
                    [id]="optionId(i)"
                    role="option"
                    [attr.aria-selected]="highlightedIndex() === i"
                    [class]="optionClass(i)"
                    class="border-b border-gray-100 last:border-b-0 dark:border-zinc-800"
                    (click)="selectUser(user)">
                    <div
                      class="size-8 shrink-0 overflow-hidden rounded-full bg-gray-200 ring-1 ring-gray-200 dark:bg-zinc-700 dark:ring-zinc-600">
                      @if (user.photoURL) {
                        <img
                          [src]="imageService.getImageSource(user.photoURL, 'gallery')"
                          alt=""
                          class="size-full object-cover" />
                      } @else {
                        <div
                          class="flex size-full items-center justify-center text-secondary"
                          aria-hidden="true">
                          <svg class="size-5" viewBox="0 0 24 24" fill="currentColor">
                            <path
                              d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z" />
                          </svg>
                        </div>
                      }
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="m-0 truncate text-sm font-medium text-primary">{{ user.fullName }}</p>
                      <p class="m-0 truncate text-xs text-secondary">
                        {{ user.email }}@if (userHandle(user)) { · {{ userHandle(user) }} }
                      </p>
                    </div>
                    <span
                      class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-zinc-700 dark:text-zinc-300">
                      {{ formatUserType(user.userType) }}
                    </span>
                  </li>
                }
              </ul>
            }
          }
        </div>
      }
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <div>
        <label for="outbound-category" class="block text-sm font-medium text-primary">Category</label>
        <select
          id="outbound-category"
          [(ngModel)]="category"
          class="mt-1 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary">
          <option value="">Select a category</option>
          @for (option of categoryOptions; track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
          }
        </select>
      </div>

      <div>
        <label for="outbound-assignee" class="block text-sm font-medium text-primary">Assign to</label>
        <select
          id="outbound-assignee"
          [(ngModel)]="assignedAdminUserId"
          class="mt-1 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary">
          <option value="">Unassigned</option>
          @for (admin of admins(); track admin.userId) {
            <option [value]="admin.userId">{{ adminLabel(admin) }}</option>
          }
        </select>
      </div>
    </div>

    <div>
      <label for="outbound-subject" class="block text-sm font-medium text-primary">Subject</label>
      <input
        id="outbound-subject"
        type="text"
        [(ngModel)]="subject"
        placeholder="What is this about?"
        class="mt-1 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary" />
    </div>

    @if (templates().length > 0) {
      <div>
        <label for="outbound-template" class="block text-sm font-medium text-primary">
          Start from a template
        </label>
        <select
          id="outbound-template"
          [ngModel]="selectedTemplateId"
          (ngModelChange)="applyTemplate($event)"
          class="mt-1 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary">
          <option value="">No template</option>
          @for (template of templates(); track template.id) {
            <option [value]="template.id">{{ template.label }}</option>
          }
        </select>
      </div>
    }

    <div>
      <label for="outbound-message" class="block text-sm font-medium text-primary">Message</label>
      <textarea
        id="outbound-message"
        rows="6"
        [(ngModel)]="message"
        placeholder="Write the first message of the conversation"
        class="mt-1 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-primary"></textarea>
    </div>

    <app-support-attachment-input
      [attachments]="attachments"
      (attachmentsChange)="onAttachmentsChange($event)" />

    <fieldset class="rounded-md border border-gray-200 dark:border-zinc-700 px-3 py-3">
      <legend class="px-1 text-sm font-medium text-primary">Notify via</legend>

      @if (!selectedUser()) {
        <p class="m-0 text-xs text-secondary">Select someone to see how they can be notified.</p>
      } @else {
        <div class="flex flex-wrap gap-4">
          <label class="flex items-center gap-2 text-sm text-primary">
            <input
              type="checkbox"
              [checked]="alertPreferences.sms"
              [disabled]="!hasMobile()"
              (change)="onSmsChange($event)"
              class="rounded border-gray-300 dark:border-zinc-600" />
            SMS
          </label>
          <label class="flex items-center gap-2 text-sm text-primary">
            <input
              type="checkbox"
              [checked]="alertPreferences.whatsapp"
              [disabled]="!hasMobile()"
              (change)="onWhatsappChange($event)"
              class="rounded border-gray-300 dark:border-zinc-600" />
            WhatsApp
          </label>
          <label class="flex items-center gap-2 text-sm text-primary">
            <input
              type="checkbox"
              [checked]="alertPreferences.email"
              [disabled]="!hasEmail()"
              (change)="onEmailChange($event)"
              class="rounded border-gray-300 dark:border-zinc-600" />
            Email
          </label>
          <label class="flex items-center gap-2 text-sm text-primary">
            <input
              type="checkbox"
              [checked]="alertPreferences.push"
              [disabled]="!hasPushDevice()"
              (change)="onPushChange($event)"
              class="rounded border-gray-300 dark:border-zinc-600" />
            Push
          </label>
        </div>

        @if (!hasMobile()) {
          <p class="mt-2 text-xs text-secondary">
            No mobile number on file, so SMS and WhatsApp are unavailable.
          </p>
        }
        @if (!hasPushDevice()) {
          <p class="mt-2 text-xs text-secondary">
            No app install on this account, so push is unavailable.
          </p>
        }
        @if (!hasEmail()) {
          <p class="mt-2 text-xs text-secondary">No email address on file, so email is unavailable.</p>
        }
      }
    </fieldset>

    @if (error()) {
      <p class="text-sm text-red-600" role="alert">{{ error() }}</p>
    }

    <div class="mt-2 flex justify-end gap-2 border-t border-gray-100 dark:border-zinc-800 pt-3">
      <button type="button" class="btn-basic-sm btn-secondary" (click)="close()">Cancel</button>
      <button
        type="button"
        class="btn-basic-sm btn-primary disabled:opacity-50"
        [disabled]="submitting()"
        (click)="submit()">
        {{ submitting() ? 'Sending...' : 'Send ticket' }}
      </button>
    </div>
  </div>
</app-dialog>
